/**
 * LA FICHE APPARTIENT À LA VERSION — module PUR, MIROIR de
 * `backend/src/fiche-version.ts` (RQ, 10-11/10/2026).
 *
 * Les colonnes du document portent ce qui S'APPLIQUE ; chaque version porte SA
 * fiche (`versions[].fiche`), figée quand elle compte. Pendant une révision, la
 * saisie des rubriques VERSIONNÉES va dans la fiche de la version CANDIDATE et
 * n'atteint le document qu'à l'approbation.
 *
 * ⚠️ L'INTITULÉ et les ÉTIQUETTES n'en font pas partie (décision RQ du
 * 11/10/2026) : une coquille de titre et un reclassement se corrigent tout de
 * suite, partout. L'intitulé reste ARCHIVÉ par la version — une archive garde le
 * titre sous lequel elle a été signée (`ficheAffichee`) ; les étiquettes ne sont
 * plus une rubrique de fiche du tout.
 *
 * Une version sans fiche archivée (avant le 10/10/2026) retombe sur celle du
 * document, et l'écran le DIT (`PHRASE_FICHE_ABSENTE`).
 *
 * ⚠️ Le serveur fait foi : il route la correction et gèle les fiches
 * (`ged.corrigerMetadonnees`, `ged.approve`, `ged.startRevision`). Ce module
 * sert à AFFICHER la bonne fiche et à nommer ce qui change d'une version à
 * l'autre. Le test `backend/tests/fiche-par-version.test.js` vérifie que les
 * deux fichiers ne divergent pas.
 */
import type { GedDocument, GedVersion } from './types';

/** Tout ce dont ce module a besoin d'un document : ses rubriques et ses versions. */
type Doc = Partial<GedDocument> | null | undefined;
type Ver = (Partial<GedVersion> & { fiche?: Record<string, unknown> | null }) | null | undefined;
type Any = Record<string, unknown>;

const lire = (o: unknown, c: string): unknown => (o ? (o as Any)[c] : undefined);


/**
 * LES RUBRIQUES QUI APPARTIENNENT À LA VERSION — deux listes, et c'est tout
 * l'intérêt : la route de correction, le gel à l'approbation, l'affichage et
 * les écarts dérivent tous d'ici. Trois copies divergeraient au premier ajout,
 * et le champ oublié serait jeté en silence.
 *
 * ⚠️ Ce qui n'est NI dans l'une NI dans l'autre, et pourquoi :
 *  · `code` — identifie le document dans le journal chaîné, immuable ;
 *  · `classe` (natif / importé) — change le RÉGIME du document (circuit ou
 *    adoption), pas sa description : une version ne peut pas être « importée »
 *    pendant que le document est « natif » ;
 *  · `telechargementRestreint` — restriction de licence du fonds externe, qui
 *    vaut pour l'exemplaire déposé, pas pour une version du circuit ;
 *  · `tags` (étiquettes) — classement libre pour RETROUVER un document, posé et
 *    retiré à tout moment (décision RQ du 11/10/2026, voir `estVersionne`) ;
 *  · `audience`, `diffusion`, `statut`, les dates de vigueur — décidés par le
 *    circuit, jamais saisis.
 *
 * ── DEUX LISTES, ET LA DIFFÉRENCE EST LA DÉCISION DU 11/10/2026 ────────────
 *
 * `CHAMPS_VERSIONNES` : ce dont la SAISIE va au chantier pendant une révision,
 * et qui n'atteint le document qu'à l'approbation. Ce sont les rubriques
 * OPPOSABLES — celles qu'un évaluateur lit sur l'exemplaire : type, processus,
 * discipline, périmètre, site, périodicité de revue, références normatives,
 * mots-clés, cotes du classeur. Les changer, c'est changer ce que le document
 * exige : cela passe par la vérification et l'approbation.
 *
 * `CHAMPS_FICHE` : ce qu'une version ARCHIVE, pour pouvoir se relire telle
 * qu'elle était. Elle ajoute l'INTITULÉ, qui n'est pas versionné à la saisie.
 */
export const CHAMPS_VERSIONNES = [
  'docType', 'processus', 'discipline', 'perimetre',
  'perimetrePostes', 'perimetreFonctions', 'site', 'periodicite',
  'referencesNorm', 'motsCles', 'codesClasseur',
] as const;

export const CHAMPS_FICHE = ['titre', ...CHAMPS_VERSIONNES] as const;

export type ChampFiche = typeof CHAMPS_FICHE[number];

/**
 * ⚠️ L'INTITULÉ SE CORRIGE TOUT DE SUITE, PARTOUT (décision RQ du 11/10/2026).
 *
 * Il avait d'abord été versionné comme le reste. Mais une coquille dans un
 * titre est une coquille : la laisser visible dans les listes, la recherche et
 * le cartouche jusqu'à l'approbation de la révision — des semaines — pour la
 * seule raison qu'une révision est ouverte, c'est faire payer au lecteur une
 * règle écrite pour les rubriques opposables.
 *
 * Il reste dans `CHAMPS_FICHE` : la fiche d'une version révolue conserve le
 * titre sous lequel elle a été SIGNÉE, sans quoi l'exemplaire archivé d'une
 * v1.0 porterait un intitulé qu'elle n'a jamais eu. Voir `ficheAffichee`.
 *
 * ⚠️ LES ÉTIQUETTES NE SONT PLUS UNE RUBRIQUE DE FICHE DU TOUT (même
 * décision) : elles servent à RETROUVER un document, pas à le décrire. Les
 * figer par version fermait le champ pendant toute une révision — on ne pouvait
 * plus reclasser ce qui s'applique. Elles vivent sur le document
 * (`ged-document.tags`), sans version et sans circuit.
 */
export const estVersionne = (c: unknown): boolean =>
  typeof c === 'string' && (CHAMPS_VERSIONNES as readonly string[]).includes(c);

/** Le nom que la RQ lit à l'écran et au journal, par rubrique. */
export const LIBELLE_CHAMP: Record<ChampFiche, string> = {
  titre: 'Intitulé',
  docType: 'Type',
  processus: 'Processus',
  discipline: 'Discipline',
  perimetre: 'Périmètre',
  perimetrePostes: 'Périmètre — postes',
  perimetreFonctions: 'Périmètre — fonctions',
  site: 'Site',
  periodicite: 'Périodicité de revue',
  referencesNorm: 'Références normatives',
  motsCles: 'Mots-clés',
  codesClasseur: 'Origine au classeur',
};

export type Fiche = Partial<Record<ChampFiche, unknown>>;

const estChamp = (c: unknown): c is ChampFiche =>
  typeof c === 'string' && (CHAMPS_FICHE as readonly string[]).includes(c);

/** Les rubriques du DOCUMENT : ce qui s'applique aujourd'hui. */
export function ficheCourante(doc: Doc): Fiche {
  const f: Fiche = {};
  for (const c of CHAMPS_FICHE) f[c] = lire(doc, c);
  return f;
}

/**
 * Cette version porte-t-elle sa propre fiche ?
 *
 * `false` ne veut pas dire « pas de rubriques » : il veut dire « on ne sait pas
 * ce qu'elles étaient ». L'écran doit l'écrire — voir `PHRASE_FICHE_ABSENTE`.
 */
export function ficheArchivee(version: Ver): boolean {
  const f = version?.fiche;
  return !!f && typeof f === 'object' && !Array.isArray(f) && Object.keys(f).length > 0;
}

/**
 * Les rubriques de CETTE version : sa fiche quand elle en a une, celle du
 * document sinon (repli avoué, cf. `ficheArchivee`).
 *
 * Le repli est fait rubrique par rubrique : une fiche écrite avant l'ajout
 * d'une rubrique neuve ne doit pas rendre cette rubrique vide — elle n'a rien
 * à en dire, c'est le document qui répond.
 */
export function ficheDeVersion(doc: Doc, version: Ver): Fiche {
  const base = ficheCourante(doc);
  const f = (version?.fiche || {}) as Any;
  if (!ficheArchivee(version)) return base;
  const sortie: Fiche = {};
  for (const c of CHAMPS_FICHE) {
    sortie[c] = Object.prototype.hasOwnProperty.call(f, c) ? f[c] : base[c];
  }
  return sortie;
}

/**
 * CE QUE LA FICHE AFFICHE POUR LA VERSION LUE — et c'est ici que les deux
 * listes se séparent (décision RQ du 11/10/2026) :
 *
 *  · rubriques VERSIONNÉES → celles de la version lue (son archive) ;
 *  · rubriques NON versionnées (l'intitulé) → celles du DOCUMENT, parce
 *    qu'elles s'appliquent dès leur correction… SAUF sur une version RÉVOLUE,
 *    où c'est le titre signé qui fait foi.
 *
 * ⚠️ `obsolete` et non « ce n'est pas la version applicable » : la version
 * CANDIDATE n'est pas révolue — son intitulé est celui du document, et le lui
 * figer ferait réapparaître, sur l'écran du chantier, la coquille qu'on vient
 * de corriger.
 */
export function ficheAffichee(doc: Doc, version: Ver): Fiche {
  const archivee = ficheDeVersion(doc, version);
  const courante = ficheCourante(doc);
  const revolue = !!version?.obsolete;
  const sortie: Fiche = {};
  for (const c of CHAMPS_FICHE) {
    sortie[c] = (estVersionne(c) || revolue) ? archivee[c] : courante[c];
  }
  return sortie;
}

export const PHRASE_FICHE_ABSENTE =
  'Les rubriques de cette version n’ont pas été archivées (elles ne le sont que depuis le 10/10/2026) : '
  + 'celles affichées sont celles du document aujourd’hui, et ont pu changer depuis.';

/**
 * OÙ S'ÉCRIT UNE CORRECTION DE RUBRIQUE.
 *
 * `candidate` dès qu'une version s'applique ET qu'une autre est en chantier :
 * la correction prépare la version suivante et n'atteint le document qu'à
 * l'approbation. `document` partout ailleurs — un document encore à rédiger n'a
 * pas de version applicable à préserver, et la corriger est sans conséquence
 * pour qui que ce soit.
 *
 * ⚠️ On ne lit PAS le statut : une révision soumise porte `averifier` ou
 * `aapprouver` alors que l'ancienne version s'applique toujours (même piège que
 * `revisionEnCours`, cf. `version-en-cours.ts`). Et une REPRISE APRÈS RETRAIT
 * n'est pas une révision : la version retirée ne s'applique plus, il n'y a donc
 * rien à préserver — la correction va au document.
 */
export function cibleFiche(doc: Doc): 'document' | 'candidate' {
  const versions = (Array.isArray(doc?.versions) ? doc?.versions : []) as GedVersion[];
  const appl = versions.find((v) => v && v.dateVigueur && !v.obsolete);
  const cand = versions.find((v) => v && !v.dateVigueur && !v.obsolete);
  if (!appl || !cand || cand.reprisApresRetrait) return 'document';
  return 'candidate';
}

const normalise = (v: unknown): string => {
  if (Array.isArray(v)) return JSON.stringify(v.map((x) => String(x ?? '')));
  if (v === null || v === undefined) return '';
  return String(v);
};

/** Deux valeurs de rubrique disent-elles la même chose ? (`null` ≡ `''` ≡ absent) */
export const memeValeur = (a: unknown, b: unknown): boolean => normalise(a) === normalise(b);

/**
 * Ce qui change d'une fiche à l'autre, rubrique par rubrique — pour l'écran
 * (« modifié dans la v2 »), pour le journal de l'approbation, et pour refuser
 * une correction qui ne corrige rien.
 */
export function ecartsFiche(avant: Fiche, apres: Fiche): { champ: ChampFiche; avant: unknown; apres: unknown }[] {
  const sortie: { champ: ChampFiche; avant: unknown; apres: unknown }[] = [];
  for (const c of CHAMPS_FICHE) {
    if (!memeValeur(avant?.[c], apres?.[c])) sortie.push({ champ: c, avant: avant?.[c], apres: apres?.[c] });
  }
  return sortie;
}

/** Une valeur de rubrique, lisible dans une phrase de journal. */
export const renduValeur = (v: unknown): string =>
  (Array.isArray(v) ? (v.length ? v.map((x) => String(x)).join(', ') : '—') : String(v ?? '') || '—');

/**
 * Ce qu'une correction écrit DANS LA VERSION : les seules rubriques
 * versionnées, assainies.
 *
 * ⚠️ Deux filtres en un, et les deux comptent. Sans le catalogue, un corps de
 * requête écrirait n'importe quelle clé dans le JSON des versions, qui n'a pas
 * de schéma pour l'en empêcher. Et sans la restriction aux rubriques
 * VERSIONNÉES, l'intitulé corrigé pendant une révision serait figé dans le
 * chantier — puis réécrit sur le document à l'approbation, avec une valeur
 * périmée depuis des semaines.
 */
export function ficheFiltree(brut: unknown): Fiche {
  const f: Fiche = {};
  for (const [k, v] of Object.entries((brut || {}) as Any)) if (estChamp(k) && estVersionne(k)) f[k] = v;
  return f;
}
